import { FieldMergeFunction, FieldPolicy, InMemoryCacheConfig, Reference } from '@apollo/client';

import { appointmentDaysTypePolicy } from '~/hooks/appointment-days';

interface CacheEdge {
    node?: Reference & { id?: string };
}

const simpleMerge: FieldMergeFunction = (existing, incoming, { mergeObjects }) => {
    return mergeObjects(existing, incoming);
};

const chatMessagesPagination: FieldPolicy = {
    keyArgs: false,
    merge(existing, incoming, { args }) {
        if (!existing) {
            return incoming;
        }

        const existingEdges = existing.edges ?? [];
        const incomingEdges = incoming.edges ?? [];
        const getId = (e: CacheEdge) => e?.node?.__ref ?? e?.node?.id;

        if (!args?.after) {
            // Subscription update or refetch (no cursor) — merge incoming edges
            // with existing, keeping older paginated edges that aren't in incoming
            const incomingIds = new Set(incomingEdges.map(getId));
            const keptExistingEdges = existingEdges.filter((e: CacheEdge) => !incomingIds.has(getId(e)));

            return {
                ...incoming,
                edges: [...incomingEdges, ...keptExistingEdges],
                pageInfo: {
                    ...incoming.pageInfo,
                    hasNextPage: existing.pageInfo?.hasNextPage ?? incoming.pageInfo?.hasNextPage,
                    endCursor: existing.pageInfo?.endCursor ?? incoming.pageInfo?.endCursor
                }
            };
        }

        // fetchMore: deduplicate and append incoming edges
        const existingIds = new Set(existingEdges.map(getId));
        const newEdges = incomingEdges.filter((e: CacheEdge) => !existingIds.has(getId(e)));

        return {
            ...incoming,
            edges: [...existingEdges, ...newEdges],
            pageInfo: incoming.pageInfo
        };
    },
    read(existing) {
        return existing;
    }
};

export const cacheConfig: InMemoryCacheConfig = {
    possibleTypes: {
        ChatAttachment: ['ChatMessageAttachmentNode', 'ChatMessageIdAttachmentNode', 'ChatMessageInteractionNode']
    },
    typePolicies: {
        Query: {
            fields: {
                root: {
                    merge: simpleMerge
                }
            }
        },
        ChatNode: {
            fields: {
                messages: chatMessagesPagination,
                messageInteractions: {
                    merge: false
                },
                notifications: {
                    merge: false
                }
            }
        },
        ChatMessageNode: {
            fields: {
                attachmentsV2: {
                    merge: false
                }
            }
        },
        // Temporary fix to 'missing field chat' error from apollo when receiving subscription confirmation
        // message until https://github.com/apollographql/apollo-client/issues/8677 is fixed.
        Subscription: {
            fields: {
                chat: {
                    read(chat) {
                        return chat ?? null;
                    }
                }
            }
        },
        CalendarReservationAccessNode: {
            merge: simpleMerge,
            fields: {
                appointmentDays: appointmentDaysTypePolicy
            }
        }
    }
};
